import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Button, Modal, TextField, FormControlLabel, Switch,
  Dialog, DialogTitle, DialogActions, FormControl, InputLabel, Select,
  MenuItem, IconButton, Grid, Paper, Autocomplete, Chip
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { getAppUserAllById } from '../../api/appWorkplacesUsersApi.js';
import { getAppKeywordDomainById, getAppKeywords } from '../../api/appKeyWordsApi.js';

function SpecialRuleModal({ openModal, selectedRule, handleCloseModal, handleSaveRule, templates, keywords: propKeywords = [], domainId }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [signatureTemplateId, setSignatureTemplateId] = useState('');
  const [priority, setPriority] = useState(1);
  const [isActive, setIsActive] = useState(true);
  const [enableEmails, setEnableEmails] = useState(false);
  const [enableConditions, setEnableConditions] = useState(false);
  const [emails, setEmails] = useState([]);
  const [conditionOperator, setConditionOperator] = useState('AND');
  const [conditionRules, setConditionRules] = useState([]);

  const [isNewRule, setIsNewRule] = useState(false);
  const [changesMade, setChangesMade] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [emailSuggestions, setEmailSuggestions] = useState([]);
  const [availableKeywords, setAvailableKeywords] = useState(propKeywords || []);

  useEffect(() => {
    if (propKeywords && propKeywords.length > 0) {
      setAvailableKeywords(propKeywords);
    }
  }, [propKeywords]);

  useEffect(() => {
    if (openModal && domainId) {
      getAppUserAllById(domainId)
        .then(response => {
          const emailsList = response.data ? response.data.map(u => u.email.trim().toLowerCase()) : [];
          const uniqueEmails = [...new Set(emailsList)].filter(e => e).sort();
          setEmailSuggestions(uniqueEmails);
        })
        .catch(err => console.error('Error fetching domain users for email suggestions:', err));

      if (!propKeywords || propKeywords.length === 0) {
        getAppKeywordDomainById(domainId)
          .then(response => {
            if (response.data && response.data.length > 0) {
              setAvailableKeywords(response.data);
            } else {
              getAppKeywords()
                .then(res => setAvailableKeywords(res.data || []))
                .catch(() => setAvailableKeywords([]));
            }
          })
          .catch(() => {
            getAppKeywords()
              .then(res => setAvailableKeywords(res.data || []))
              .catch(() => setAvailableKeywords([]));
          });
      }
    }
  }, [openModal, domainId, propKeywords]);

  const keywordOptions = useMemo(() => {
    const list = Array.isArray(availableKeywords) ? availableKeywords : [];
    const names = list
      .map(k => (typeof k === 'string' ? k.trim() : k?.name?.trim()))
      .filter(Boolean);
    return [...new Set(names)].sort((a, b) => a.localeCompare(b));
  }, [availableKeywords]);

  const availableTemplates = useMemo(() => {
    if (!Array.isArray(templates)) return [];
    return templates
      .filter(t => Boolean(t?.is_active) || t?.is_active === 'true' || (!isNewRule && t?.id === signatureTemplateId))
      .sort((a, b) => {
        const nameA = a?.template_name || '';
        const nameB = b?.template_name || '';
        return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' }) || nameA.localeCompare(nameB);
      });
  }, [templates, isNewRule, signatureTemplateId]);

  useEffect(() => {
    if (selectedRule?.id) {
      setName(selectedRule.name || '');
      setDescription(selectedRule.description || '');
      setSignatureTemplateId(selectedRule.signature_template_id || '');
      setPriority(selectedRule.priority || 1);
      setIsActive(!!selectedRule.is_active);
      const ruleEmails = selectedRule.emails || [];
      setEmails(ruleEmails);
      setEnableEmails(ruleEmails.length > 0);
      
      const cond = selectedRule.conditions || {};
      const rules = cond.rules || [];
      setConditionOperator(cond.operator || 'AND');
      setConditionRules(rules);
      setEnableConditions(rules.length > 0);

      setIsNewRule(false);
      setChangesMade(false);
    } else {
      setName('');
      setDescription('');
      setSignatureTemplateId('');
      setPriority(1);
      setIsActive(true);
      setEmails([]);
      setEnableEmails(false);
      setConditionOperator('AND');
      setConditionRules([]);
      setEnableConditions(false);
      
      setIsNewRule(true);
      setChangesMade(false);
    }
  }, [selectedRule, openModal]);

  const handleActiveChange = (event) => {
    setIsActive(event.target.checked);
    setChangesMade(true);
  };

  const handleInputChange = (setter) => (event) => {
    setter(event.target.value);
    setChangesMade(true);
  };

  const handleAddCondition = () => {
    setConditionRules([...conditionRules, { field: '', operator: '==', value: '' }]);
    setChangesMade(true);
  };

  const handleRemoveCondition = (index) => {
    const newRules = [...conditionRules];
    newRules.splice(index, 1);
    setConditionRules(newRules);
    setChangesMade(true);
  };

  const handleConditionChange = (index, field, value) => {
    const newRules = [...conditionRules];
    newRules[index] = { ...newRules[index], [field]: value };
    setConditionRules(newRules);
    setChangesMade(true);
  };

  const onSave = () => {
    const ruleData = {
      name,
      description,
      signature_template_id: signatureTemplateId,
      priority: parseInt(priority, 10),
      is_active: isActive,
      emails: enableEmails ? emails.map(e => e.trim()).filter(e => e) : [],
      conditions: {
        operator: conditionOperator,
        rules: enableConditions ? conditionRules.filter(r => r.field && r.operator && r.value) : []
      }
    };
    handleSaveRule(ruleData, isNewRule);
    setChangesMade(false);
  };

  const handleCloseRequest = () => {
    if (changesMade) {
      setConfirmClose(true);
    } else {
      handleCloseModal();
    }
  };

  const confirmDiscardChanges = () => {
    setConfirmClose(false);
    handleCloseModal();
  };

  return (
    <>
      <Modal open={openModal} onClose={handleCloseRequest}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '80vw',
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            overflow: 'auto',
          }}
        >
          <Typography variant="h6" component="h2" gutterBottom>
            {isNewRule ? 'Crear Regla Especial' : `Editar Regla Especial ${selectedRule?.id ? `(ID: ${selectedRule.id})` : ''}`}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Nombre de la regla"
                variant="outlined"
                margin="normal"
                value={name}
                onChange={handleInputChange(setName)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Prioridad"
                variant="outlined"
                margin="normal"
                type="number"
                value={priority}
                onChange={handleInputChange(setPriority)}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                variant="outlined"
                margin="normal"
                multiline
                rows={2}
                value={description}
                onChange={handleInputChange(setDescription)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="template-select-label">Template de Firma</InputLabel>
                <Select
                  labelId="template-select-label"
                  value={signatureTemplateId}
                  label="Template de Firma"
                  onChange={handleInputChange(setSignatureTemplateId)}
                >
                  {availableTemplates.map(t => (
                    <MenuItem key={t.id} value={t.id}>{t.template_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} display="flex" alignItems="center">
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={handleActiveChange}
                    color="primary"
                  />
                }
                label="Regla Activa"
              />
            </Grid>

            <Grid item xs={12} display="flex" alignItems="center" gap={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={enableEmails}
                    onChange={(e) => { setEnableEmails(e.target.checked); setChangesMade(true); }}
                    color="primary"
                  />
                }
                label="Emails Específicos"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={enableConditions}
                    onChange={(e) => { setEnableConditions(e.target.checked); setChangesMade(true); }}
                    color="primary"
                  />
                }
                label="Condiciones de Atributos"
              />
            </Grid>

            {enableEmails && (
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={emailSuggestions}
                  value={emails}
                  filterOptions={(options, state) => {
                    const query = state.inputValue.trim().toLowerCase();
                    return options.filter(option => 
                      option.toLowerCase().includes(query)
                    );
                  }}
                  onChange={(event, newValue) => {
                    setEmails(newValue);
                    setChangesMade(true);
                  }}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                      <Chip
                        label={option}
                        {...getTagProps({ index })}
                        key={option}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Emails específicos"
                      placeholder="Escribe un email y presiona Enter o Tab"
                      variant="outlined"
                      margin="normal"
                      helperText="Si aplica directamente a ciertos correos"
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>

          {enableConditions && (
            <Box mt={3} mb={2}>
              <Typography variant="subtitle1" fontWeight="bold">Condiciones de atributos</Typography>
              <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Operador Lógico</InputLabel>
                    <Select
                      value={conditionOperator}
                      label="Operador Lógico"
                      onChange={handleInputChange(setConditionOperator)}
                    >
                      <MenuItem value="AND">AND (Todas)</MenuItem>
                      <MenuItem value="OR">OR (Alguna)</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={handleAddCondition}>
                    Agregar Condición
                  </Button>
                </Box>

                {conditionRules.map((rule, idx) => {
                  const currentField = rule.field ? rule.field.trim() : '';
                  const rowOptions = [...keywordOptions];
                  if (currentField && !rowOptions.includes(currentField)) {
                    rowOptions.unshift(currentField);
                  }

                  return (
                    <Box key={idx} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id={`attribute-select-label-${idx}`}>Atributo</InputLabel>
                        <Select
                          labelId={`attribute-select-label-${idx}`}
                          id={`attribute-select-${idx}`}
                          value={rule.field || ''}
                          label="Atributo"
                          onChange={(e) => handleConditionChange(idx, 'field', e.target.value)}
                        >
                          <MenuItem value="">
                            <em>Seleccionar atributo</em>
                          </MenuItem>
                          {rowOptions.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel id={`operator-select-label-${idx}`}>Operador</InputLabel>
                        <Select
                          labelId={`operator-select-label-${idx}`}
                          value={rule.operator}
                          label="Operador"
                          onChange={(e) => handleConditionChange(idx, 'operator', e.target.value)}
                        >
                          <MenuItem value="==">Igual (==)</MenuItem>
                          <MenuItem value="!=">Distinto (!=)</MenuItem>
                          <MenuItem value="in">En lista (in)</MenuItem>
                          <MenuItem value="not_in">No en lista (not in)</MenuItem>
                          <MenuItem value="contains">Contiene</MenuItem>
                          <MenuItem value=">">Mayor que (&gt;)</MenuItem>
                          <MenuItem value="<">Menor que (&lt;)</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        sx={{ flexGrow: 1 }}
                        label={rule.operator === 'in' || rule.operator === 'not_in' ? 'Valores separados por comas' : 'Valor (ej: Gerente)'}
                        placeholder={rule.operator === 'in' || rule.operator === 'not_in' ? 'ej: 20900, 20910, 20911' : 'Valor'}
                        value={rule.value}
                        onChange={(e) => handleConditionChange(idx, 'value', e.target.value)}
                      />
                      <IconButton onClick={() => handleRemoveCondition(idx)} color="error" aria-label="Eliminar condición">
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  );
                })}

              </Paper>
            </Box>
          )}

          <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
            <Button onClick={handleCloseRequest} color="secondary" variant="outlined">
              Cancelar
            </Button>
            <Button
              onClick={onSave}
              color="primary"
              variant="contained"
              disabled={!name || !signatureTemplateId}
            >
              Guardar Regla
            </Button>
          </Box>
        </Box>
      </Modal>

      <Dialog open={confirmClose} onClose={() => setConfirmClose(false)}>
        <DialogTitle>Descartar cambios</DialogTitle>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setConfirmClose(false)} color="primary" variant="outlined">
            Continuar editando
          </Button>
          <Button onClick={confirmDiscardChanges} color="error" variant="contained">
            Descartar cambios
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default SpecialRuleModal;
